import { useMutation, useQueryClient } from "@tanstack/react-query";

import { accessManagementService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export function useGrantRolePermission() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      roleCode,
      permissionCode,
    }: {
      roleCode: string;
      permissionCode: string;
    }) =>
      ensureOk(
        await accessManagementService.grantRolePermission(
          roleCode,
          permissionCode
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-role-permissions-roles"],
      });
      // The User Access modal derives role-inherited permissions from its
      // own roles catalog — keep it in step with role edits made here.
      queryClient.invalidateQueries({
        queryKey: ["admin-user-access-roles"],
      });
    },
  });

  return {
    grantRolePermission: mutateAsync,
    isGrantingRolePermission: isPending,
  };
}

export function useRevokeRolePermission() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      roleCode,
      permissionCode,
    }: {
      roleCode: string;
      permissionCode: string;
    }) =>
      ensureOk(
        await accessManagementService.revokeRolePermission(
          roleCode,
          permissionCode
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-role-permissions-roles"],
      });
      // The User Access modal derives role-inherited permissions from its
      // own roles catalog — keep it in step with role edits made here.
      queryClient.invalidateQueries({
        queryKey: ["admin-user-access-roles"],
      });
    },
  });

  return {
    revokeRolePermission: mutateAsync,
    isRevokingRolePermission: isPending,
  };
}

export function useAssignUserRole() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      cmsUserId,
      roleCode,
    }: {
      cmsUserId: number;
      roleCode: string;
    }) =>
      ensureOk(
        await accessManagementService.assignUserRole(cmsUserId, roleCode)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user-access-users"] });
    },
  });

  return { assignUserRole: mutateAsync, isAssigningUserRole: isPending };
}

export function useGrantUserPermission() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      cmsUserId,
      permissionCode,
    }: {
      cmsUserId: number;
      permissionCode: string;
    }) =>
      ensureOk(
        await accessManagementService.grantUserPermission(
          cmsUserId,
          permissionCode
        )
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-user-access-user-permissions", variables.cmsUserId],
      });
    },
  });

  return {
    grantUserPermission: mutateAsync,
    isGrantingUserPermission: isPending,
  };
}

export function useRevokeUserPermission() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      cmsUserId,
      permissionCode,
    }: {
      cmsUserId: number;
      permissionCode: string;
    }) =>
      ensureOk(
        await accessManagementService.revokeUserPermission(
          cmsUserId,
          permissionCode
        )
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-user-access-user-permissions", variables.cmsUserId],
      });
    },
  });

  return {
    revokeUserPermission: mutateAsync,
    isRevokingUserPermission: isPending,
  };
}
